import { useState, useEffect, useCallback } from 'react';
// Import from essentialExports for guaranteed compatibility
import { 
  initializeRAG, 
  getStoredAPIKey, 
  getRAGStore, 
  searchKnowledgeUnits,
  sendRagQuery,
  storeAPIKey as storeAPIKeyFn
} from './essentialExports';
import type { RAGQueryResult, RAGConfig, SecurityKnowledgeUnit } from './ragTypes';
import { QueryType } from './ragTypes';

interface RAGHookState {
  loading: boolean;
  initialized: boolean;
  error: string | null;
  result: RAGQueryResult | null;
  searching: boolean;
  searchResults: SecurityKnowledgeUnit[];
  conversationHistory: {
    query: string;
    queryType: QueryType;
    result: RAGQueryResult | null;
    timestamp: Date;
  }[];
}

interface RAGHookResult extends RAGHookState {
  initialize: (config?: RAGConfig) => Promise<boolean>;
  query: (question: string, type?: QueryType) => Promise<RAGQueryResult | null>;
  search: (searchTerm: string) => void;
  setApiKey: (apiKey: string) => void;
  analyzeQueryType: (question: string) => QueryType;
  clearHistory: () => void;
  debugInfo: () => any;
}

// Keywords that help identify query types
const QUERY_TYPE_KEYWORDS = {
  [QueryType.PAYLOAD]: ['payload', 'injection', 'xss', 'script', 'exploit code', 'sql injection', 'command injection', 'xxe', 'cve exploit', 'malicious input', 'example payload', 'injection payload'],
  [QueryType.TECHNIQUE]: ['how to', 'technique', 'method', 'approach', 'steps', 'tutorial', 'guide', 'procedure', 'process', 'implement', 'exploitation technique', 'attack technique', 'attack method'],
  [QueryType.TOOL]: ['tool', 'software', 'application', 'program', 'utility', 'script', 'framework', 'scanner', 'analyzer', 'best tool', 'recommended tool', 'security tool', 'pentesting tool'],
  [QueryType.WAF_BYPASS]: ['waf', 'bypass', 'firewall', 'evade', 'evasion', 'circumvent', 'get around', 'bypass detection', 'avoid blocking', 'bypass waf', 'evade detection', 'bypass firewall']
};

/**
 * React hook for using the RAG system in components
 */
export const useRag = (): RAGHookResult => {
  const [state, setState] = useState<RAGHookState>({
    loading: false,
    initialized: false,
    error: null,
    result: null,
    searching: false,
    searchResults: [],
    conversationHistory: []
  });

  // Initialize the RAG system
  const initialize = useCallback(async (config?: RAGConfig): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      // Use stored API key if available
      const storedApiKey = getStoredAPIKey();
      const finalConfig: RAGConfig = config || { 
        apiKey: storedApiKey || '',
        model: 'gpt-3.5-turbo'
      };
      
      console.log('Initializing RAG with config:', { ...finalConfig, apiKey: finalConfig.apiKey ? '[REDACTED]' : 'none' });
      
      // Initialize RAG store
      const success = await initializeRAG(finalConfig);
      
      console.log('RAG initialization result:', success ? 'SUCCESS' : 'FAILED');
      
      if (!success) {
        const store = getRAGStore();
        console.error('RAG initialization failed:', store.loadError);
      }
      
      setState(prev => ({
        ...prev,
        loading: false,
        initialized: success,
        error: success ? null : 'Failed to initialize RAG. Please check network connection and try again.'
      }));
      
      return success;
    } catch (error) {
      console.error('Error initializing RAG:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        initialized: false,
        error: error instanceof Error ? error.message : String(error)
      }));
      return false;
    }
  }, []);

  // Auto-initialize if API key is available in localStorage
  useEffect(() => {
    const storedApiKey = getStoredAPIKey();
    if (storedApiKey) {
      console.log('Found stored API key, auto-initializing RAG');
      initialize().then(success => {
        if (!success) {
          console.warn('Auto-initialization of RAG failed');
        }
      });
    } else {
      console.log('No stored API key found, skipping auto-initialization');
    }
  }, [initialize]);

  // Automatically analyze query type based on content
  const analyzeQueryType = useCallback((question: string): QueryType => {
    const lowerQuestion = question.toLowerCase();
    
    // Remove target domain prefix if present
    const cleanQuestion = lowerQuestion.replace(/\[target domain:.*?\]\s*/i, '');
    
    console.log('Analyzing query type for:', cleanQuestion);
    
    // First check for explicit mentions of query types
    if (cleanQuestion.includes('payload type:') || cleanQuestion.includes('need payload') || 
        cleanQuestion.includes('payload for') || cleanQuestion.includes('example payload')) {
      console.log('Detected PAYLOAD query type from explicit mention');
      return QueryType.PAYLOAD;
    }
    
    if (cleanQuestion.includes('technique for') || cleanQuestion.includes('technique type:') || 
        cleanQuestion.includes('how to technically')) {
      console.log('Detected TECHNIQUE query type from explicit mention');
      return QueryType.TECHNIQUE;
    }
    
    if (cleanQuestion.includes('tool for') || cleanQuestion.includes('tool type:') || 
        cleanQuestion.includes('recommend tool') || cleanQuestion.includes('which tool')) {
      console.log('Detected TOOL query type from explicit mention');
      return QueryType.TOOL;
    }
    
    if (cleanQuestion.includes('waf bypass') || cleanQuestion.includes('bypass waf') || 
        cleanQuestion.includes('bypass firewall')) {
      console.log('Detected WAF_BYPASS query type from explicit mention');
      return QueryType.WAF_BYPASS;
    }
    
    // Check for keyword matches with scoring
    let typeScores = {
      [QueryType.PAYLOAD]: 0,
      [QueryType.TECHNIQUE]: 0,
      [QueryType.TOOL]: 0,
      [QueryType.WAF_BYPASS]: 0,
      [QueryType.GENERAL]: 0
    };
    
    // Check for explicit query type indicators and score them
    for (const [typeKey, keywords] of Object.entries(QUERY_TYPE_KEYWORDS)) {
      keywords.forEach(keyword => {
        if (cleanQuestion.includes(keyword)) {
          typeScores[typeKey as QueryType] += 1;
          
          // Give higher scores for more specific matches
          if (cleanQuestion.includes(`${keyword} for`)) {
            typeScores[typeKey as QueryType] += 2;
          }
        }
      });
    }
    
    // Additional analysis for common patterns
    if (cleanQuestion.startsWith('how') || cleanQuestion.startsWith('what is') || cleanQuestion.startsWith('explain')) {
      typeScores[QueryType.TECHNIQUE] += 1;
    }
    
    if (cleanQuestion.includes('example') && (cleanQuestion.includes('code') || cleanQuestion.includes('payload'))) {
      typeScores[QueryType.PAYLOAD] += 2;
    }
    
    if (cleanQuestion.includes('recommend') || cleanQuestion.includes('best') || cleanQuestion.includes('suggest')) {
      if (cleanQuestion.includes('tool')) {
        typeScores[QueryType.TOOL] += 2;
      }
    }
    
    // Find the type with the highest score
    let maxScore = 0;
    let detectedType = QueryType.GENERAL;
    
    for (const [type, score] of Object.entries(typeScores)) {
      if (score > maxScore) {
        maxScore = score;
        detectedType = type as QueryType;
      }
    }
    
    console.log('Query type scores:', typeScores);
    console.log('Detected query type:', detectedType);
    
    return detectedType;
  }, []);

  // Send a query to the RAG system
  const query = useCallback(async (question: string, type: QueryType = QueryType.GENERAL): Promise<RAGQueryResult | null> => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      // Check if RAG is initialized
      const store = getRAGStore();
      if (!store.initialized) {
        console.log('RAG not initialized, attempting to initialize before query');
        const success = await initialize();
        if (!success) {
          throw new Error('RAG system is not initialized. Please initialize first.');
        }
      }
      
      // Determine queryType if not specified or if it's general
      const queryType = (type === QueryType.GENERAL) ? 
        analyzeQueryType(question) : type;
      
      console.log('Sending query:', question);
      console.log('Final query type:', queryType);
      
      // Ensure we're using the AI-powered RAG
      console.log('Checking for AI integration...');
      let usingAI = false;
      
      try {
        // Dynamic import to check if AI module is available
        const aiModule = await import('./openaiService.ts');
        if (aiModule && typeof aiModule.processRagQuery === 'function') {
          console.log('AI integration available');
          usingAI = true;
        }
      } catch (err) {
        console.warn('AI integration not available:', err);
        // Try alternative import
        try {
          const aiModuleAlt = await import('./openaiService');
          if (aiModuleAlt && typeof aiModuleAlt.processRagQuery === 'function') {
            console.log('AI integration available (alternative import)');
            usingAI = true;
          }
        } catch (altErr) {
          console.warn('Alternative AI integration also failed:', altErr);
        }
      }
      
      // Send the query to the RAG system
      const result = await sendRagQuery(question, queryType);
      
      console.log('RAG query result:', {
        answerLength: result.answer.length,
        relevantUnitsCount: result.relevantUnits.length,
        confidence: result.confidence,
        sourcesCount: result.sources.length,
        usingAI
      });
      
      // Add to conversation history
      const historyEntry = {
        query: question,
        queryType,
        result,
        timestamp: new Date()
      };
      
      setState(prev => ({
        ...prev,
        loading: false,
        result,
        error: null,
        conversationHistory: [...prev.conversationHistory, historyEntry]
      }));
      
      return result;
    } catch (error) {
      console.error('Error querying RAG:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : String(error)
      }));
      return null;
    }
  }, [analyzeQueryType, initialize]);

  // Perform a search on the knowledge base
  const search = useCallback((searchTerm: string) => {
    setState(prev => ({ ...prev, searching: true }));
    
    const store = getRAGStore();
    if (!store.initialized || !searchTerm.trim()) {
      setState(prev => ({ 
        ...prev, 
        searching: false, 
        searchResults: [],
        error: !store.initialized ? 'RAG not initialized' : null
      }));
      return;
    }
    
    try {
      console.log('Searching knowledge base for:', searchTerm);
      const results = searchKnowledgeUnits(searchTerm);
      console.log(`Search found ${results.length} results`);
      
      setState(prev => ({ 
        ...prev, 
        searching: false, 
        searchResults: results,
        error: null
      }));
    } catch (error) {
      console.error('Error searching knowledge base:', error);
      setState(prev => ({
        ...prev,
        searching: false,
        error: error instanceof Error ? error.message : String(error)
      }));
    }
  }, []);

  // Set API key
  const setApiKey = useCallback((apiKey: string) => {
    console.log('Setting new API key');
    storeAPIKeyFn(apiKey);
    
    // Re-initialize with new key
    initialize({ apiKey }).catch(error => {
      console.error('Failed to initialize with new API key:', error);
    });
  }, [initialize]);

  // Clear conversation history
  const clearHistory = useCallback(() => {
    setState(prev => ({
      ...prev,
      conversationHistory: []
    }));
  }, []);
  
  // Debug information function
  const debugInfo = useCallback(() => {
    const store = getRAGStore();
    
    // Check if AI integration is available
    let aiIntegrationStatus = 'Unknown';
    try {
      // Use dynamic import to check if AI module is available (ES modules approach)
      import('./openaiService.ts').then(() => {
        aiIntegrationStatus = 'Available';
      }).catch(() => {
        // Try alternative import
        import('./openaiService').then(() => {
          aiIntegrationStatus = 'Available (alternative)';
        }).catch(() => {
          aiIntegrationStatus = 'Not available';
        });
      });
    } catch (error) {
      aiIntegrationStatus = `Not available: ${error instanceof Error ? error.message : String(error)}`;
    }
    
    return {
      store: {
        initialized: store.initialized,
        knowledgeUnitsCount: store.knowledgeUnits?.length || 0,
        hasApiKey: !!store.apiKey,
        loadTimestamp: store.loadTimestamp,
        loadError: store.loadError
      },
      state: {
        initialized: state.initialized,
        loading: state.loading,
        error: state.error,
        conversationHistoryLength: state.conversationHistory.length,
        lastQuery: state.conversationHistory.length > 0 ? 
          state.conversationHistory[state.conversationHistory.length - 1].query : null,
        lastQueryType: state.conversationHistory.length > 0 ? 
          state.conversationHistory[state.conversationHistory.length - 1].queryType : null
      },
      aiIntegration: {
        status: aiIntegrationStatus,
        lastResponseLength: state.result?.answer.length || 0,
        lastConfidence: state.result?.confidence || 0
      }
    };
  }, [state]);

  return {
    ...state,
    initialize,
    query,
    search,
    setApiKey,
    analyzeQueryType,
    clearHistory,
    debugInfo
  };
}; 